import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, X, Camera, CurrencyInr } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_PHOTOS = 5;

/**
 * Unified dual-pricing + portfolio selector for ALL eligible professional
 * categories (Phase 3) — Chef, Photographer, Magician, Singer, Decoration
 * Team, etc. — anything except `server_waiter` / `catering_team` / `return_gift`.
 *
 * Pros enter exactly:
 *  • Up to 5 portfolio photos
 *  • Wedding price (covers marriage / engagement / reception)
 *  • Other Events price (covers everything else)
 *
 * Both prices are optional but at least one must be set, otherwise the pro
 * is hidden from every customer listing.
 */
const ProDualPriceSelector = ({ categoryLabel, onSaved }) => {
  const [photos, setPhotos] = useState([]);
  const [weddingPrice, setWeddingPrice] = useState('');
  const [otherPrice, setOtherPrice] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    const fetchExisting = async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
        const ep = data.event_prices || {};
        setWeddingPrice(ep.marriage || '');
        setOtherPrice(ep.other_events || '');
        setPhotos((data.portfolio_photos || []).slice(0, MAX_PHOTOS));
      } catch { /* ignore — first-time setup */ }
      setLoaded(true);
    };
    fetchExisting();
  }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (photos.length >= MAX_PHOTOS) {
      toast.error(`Maximum ${MAX_PHOTOS} photos`);
      return;
    }
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', file);
      const resp = await axios.post(`${BACKEND_URL}/api/upload`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setPhotos([...photos, resp.data.url]);
      toast.success('Photo uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const removePhoto = (idx) => setPhotos(photos.filter((_, i) => i !== idx));

  const handleSave = async () => {
    const wp = Number(weddingPrice || 0);
    const op = Number(otherPrice || 0);
    if (wp <= 0 && op <= 0) {
      toast.error('Enter at least one price — Wedding or Other Events');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/professionals/dual-pricing`,
        { event_prices: { marriage: wp, other_events: op }, portfolio_photos: photos },
        { withCredentials: true });
      toast.success('Pricing & portfolio saved');
      if (onSaved) onSaved({ event_prices: { marriage: wp, other_events: op }, portfolio_photos: photos });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  if (!loaded) {
    return <Card className="bg-white border-[#E8E1D5]"><CardContent className="py-6 text-sm text-text-secondary text-center">Loading...</CardContent></Card>;
  }

  return (
    <Card className="bg-white border-[#E8E1D5]" data-testid="pro-dual-price-selector">
      <CardHeader className="border-b border-[#E8E1D5]">
        <CardTitle className="font-heading text-xl">{categoryLabel} — Pricing &amp; Portfolio</CardTitle>
        <CardDescription>
          Set your two prices and showcase your work. Wedding price covers <strong>Wedding, Engagement &amp; Reception</strong>. Other Events price covers everything else. Leave a field blank to skip that event group.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5 pt-5">
        <div className="space-y-2">
          <Label className="text-xs tracking-[0.18em] uppercase font-bold text-text-secondary flex items-center gap-1">
            <Camera size={14} /> Portfolio Photos
            <span className="font-normal text-text-secondary normal-case tracking-normal">({photos.length}/{MAX_PHOTOS})</span>
          </Label>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
            {photos.map((p, i) => (
              <div key={i} className="relative aspect-square border border-[#E8E1D5] overflow-hidden group">
                <img src={p} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => removePhoto(i)}
                  className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                  data-testid={`remove-photo-${i}`}>
                  <X size={12} />
                </button>
              </div>
            ))}
            {photos.length < MAX_PHOTOS && (
              <button type="button" onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="aspect-square border-2 border-dashed border-brand-primary/40 flex flex-col items-center justify-center gap-1 hover:bg-brand-accent/10 transition disabled:opacity-50"
                data-testid="add-photo-btn">
                <Plus size={20} className="text-brand-primary" />
                <span className="text-[10px] font-bold uppercase tracking-wider text-brand-primary">
                  {uploading ? 'Uploading...' : 'Add'}
                </span>
              </button>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} data-testid="photo-input" />
          <p className="text-[10px] text-text-secondary italic">
            Photos must not contain phone numbers or email addresses.
          </p>
        </div>

        <div className="border-2 border-brand-primary/40 rounded p-3 bg-brand-accent/5 space-y-3">
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-brand-primary">Pricing</div>
          <p className="text-[11px] text-text-secondary leading-relaxed">
            Both prices are optional — fill at least one. Wedding price shows for marriage/engagement/reception listings; Other Events price shows for every other event type. You'll be hidden from listings where the matching price is left blank.
          </p>
          <div className="space-y-1">
            <Label className="text-xs font-bold text-text-secondary flex items-center gap-1">
              <CurrencyInr size={12} /> Wedding / Engagement / Reception <span className="text-text-secondary font-normal">(optional)</span>
            </Label>
            <Input type="number" min={0} value={weddingPrice}
              onChange={(e) => setWeddingPrice(e.target.value)}
              placeholder="₹ price per event for wedding-tier"
              className="rounded-none border-[#E8E1D5] h-9"
              data-testid="wedding-price-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-bold text-text-secondary flex items-center gap-1">
              <CurrencyInr size={12} /> Other Events <span className="text-text-secondary font-normal">(optional)</span>
            </Label>
            <Input type="number" min={0} value={otherPrice}
              onChange={(e) => setOtherPrice(e.target.value)}
              placeholder="₹ price per event for birthday, corporate, etc."
              className="rounded-none border-[#E8E1D5] h-9"
              data-testid="other-price-input" />
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <Button onClick={handleSave} disabled={saving}
            className="bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none"
            data-testid="save-btn">
            {saving ? 'Saving...' : 'Save Pricing & Portfolio'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProDualPriceSelector;
